import React, { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { RateImpressionProvider } from 'contexts/RateImpressionContext'
import ContactContext from 'contexts/contactContext'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import uuidV4 from 'lib/string/uuidV4Utils'
import {
  getCurrentAvailableRates,
  getPropertyCombinedRatesByRoom,
  getPropertyLowestAvailableStayPayTier,
  getPropertyRatesKey,
} from 'selectors/offerPage/offerPageSelectors'

import PropertyRoomOption from './PropertyRoomOption'
import type { RoomRateViews } from './singlePropertyUtils'
import Sheet from 'components/utils/Sheet'
import { verticalChildSpacing } from 'components/utils/spacing'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import styled from 'styled-components'
import { rem } from 'polished'

const OptionsContainer = styled(Sheet)`
  ${verticalChildSpacing[8]}

  ${mediaQueryUp.desktop} {
    ${verticalChildSpacing[32]}
  }

  ${mediaQueryDown.mobile} {
    padding-block: ${rem(8)};
  }
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer | undefined
  combinedRatesByRoom: Array<RoomRateViews>
  hasRateError: boolean
  checkIn?: string
  availabilityKey: string | undefined
  onBookNowInvalid?: () => void
  onViewPackageOptions: () => void
  lowestAvailableStayPayTier?: App.StayPayTier | null
}

function PropertyRoomOptions(props: Props) {
  const {
    property,
    offer,
    combinedRatesByRoom,
    hasRateError,
    onViewPackageOptions,
    checkIn,
    availabilityKey,
    lowestAvailableStayPayTier,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao.                                                                        */
  /************************************************************************************************/

  const intl = useIntl()
  const supportContact = useContext(ContactContext)

  const highlightedPackage = useMemo(
    () => {
      const selectedRoom = combinedRatesByRoom.find(({ isSelected }) => isSelected)
      const highlightedRoom = combinedRatesByRoom.find(({ isHighlighted }) => isHighlighted)
      // If we've selected a different room from the GHA-highlighted room, then suppress GHA
      // highlighting.
      if (!selectedRoom || selectedRoom === highlightedRoom) return highlightedRoom
    },
    [combinedRatesByRoom],
  )

  const roomListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [combinedRatesByRoom]) // regenerate id if list of rooms changes

  // Because we might promote a room with a highlighted rate to the top, we can't use list item
  // indices as the basis for our counter.
  let nextRoomPosition = 1

  return (<RateImpressionProvider availabilityKey={availabilityKey}>
    <VerticalSpacer gap={16}>
      <LayoutContainer>
        {hasRateError && <MessageBanner
          kind="warning"
          tabletAlign="center"
          heading={intl.formatMessage(
            {
              id: 'BnvLw0OFjc',
              defaultMessage: 'The third party booking system for {propertyName} is currently down',
              description: 'Error message heading when property booking system is down',
            },
            { propertyName: property.name },
          )}
        >
          {intl.formatMessage(
            {
              id: 'tGbUfKIAF/',
              defaultMessage: 'Please call our VIP team on {phoneNumber} who are ready to provide assistance to complete your booking',
              description: 'Error message body with phone number when property booking system is down',
            },
            { phoneNumber: supportContact.local.humanReadable },
          )}
        </MessageBanner>}
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional-primary"
          icon={<LineInfoCircleIcon />}
        >
          <FormattedMessage
            id="Y4WYWCYMfm"
            defaultMessage="<b>Special offer:</b> Enjoy free nights for stays longer than {count, plural, one {# day} other {# days}}. Select dates to check eligibility."
            description="Stay pay tier promotional banner message in the loyalty section"
            values={{
              count: lowestAvailableStayPayTier.stayNights,
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </MessageBanner>}
      </LayoutContainer>
      {!hasRateError && <OptionsContainer background="neutral-six" tabletBackground="none">
        {highlightedPackage && !!highlightedPackage.rates.length && <PropertyRoomOption
          property={property}
          offer={offer}
          propertyRoom={highlightedPackage.propertyRoom}
          availableRooms={highlightedPackage.availableRooms}
          rates={highlightedPackage.rates}
          roomFitsCapacity={highlightedPackage.roomFitsCapacity}
          datesAvailable={highlightedPackage.datesAvailable}
          isSelected={highlightedPackage.isSelected}
          isHighlighted
          roomRateUnmatched={highlightedPackage.roomRateUnmatched}
          onViewPackageOptions={onViewPackageOptions}
          roomListId={roomListId}
          roomPosition={nextRoomPosition++}
        />}
        {combinedRatesByRoom.map((item) => {
          if (highlightedPackage && highlightedPackage.roomId === item.roomId) {
            return null
          }
          return (<PropertyRoomOption
            key={item.roomId}
            property={property}
            offer={offer}
            propertyRoom={item.propertyRoom}
            availableRooms={item.availableRooms}
            rates={item.rates}
            roomFitsCapacity={item.roomFitsCapacity}
            datesAvailable={item.datesAvailable}
            isSelected={item.isSelected}
            onViewPackageOptions={onViewPackageOptions}
            roomListId={roomListId}
            roomPosition={nextRoomPosition++}
          />)
        })}
      </OptionsContainer>}
    </VerticalSpacer>
  </RateImpressionProvider>
  )
}

type MappedProps = Pick<Props,
  'property' |
  'offer' |
  'combinedRatesByRoom' |
  'hasRateError' |
  'checkIn' |
  'availabilityKey' |
  'lowestAvailableStayPayTier'
> & { open?: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!,
  offer: state.offer as App.HotelOffer | undefined,
  combinedRatesByRoom: getPropertyCombinedRatesByRoom(state, appState),
  hasRateError: !!getCurrentAvailableRates(state, appState)?.error,
  checkIn: state.checkInDate,
  availabilityKey: getPropertyRatesKey(state, appState),
  lowestAvailableStayPayTier: getPropertyLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOptions, preventRenderWithOpenFilter)
